import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Linkedin, Twitter, ArrowRight } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { href: '/about', label: 'About Us' },
    { href: '/services', label: 'Our Services' },
    { href: '/sectors', label: 'Sectors' },
    { href: '/why-us', label: 'Why Choose Us' },
    { href: '/contact', label: 'Contact' },
  ];

  const services = [
    'Procurement Outsourcing',
    'International Sourcing',
    'Supplier Identification',
    'Order Management',
    'Logistics Coordination',
  ];

  const sectors = [
    'Industry',
    'Agro-industry',
    'Mining',
    'Construction',
    'NGO & Institutions',
  ];

  return (
    <footer data-testid="footer" className="bg-[#0F172A] text-white noise-overlay">
      <div className="relative z-10">
        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <div className="mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_10ee391f-7fbb-4a86-9ff7-71837a72382a/artifacts/12wpelqj_7950f7b3-f97d-45fe-955c-abbad45829a7.JPG"
                  alt="Supply Hub Logo"
                  className="h-14 w-auto bg-white p-2"
                />
              </div>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                Your trusted procurement partner in Africa and beyond. Simplifying international 
                sourcing and securing every step of the purchasing process.
              </p>
              <div className="flex gap-4">
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="linkedin-link"
                  className="w-10 h-10 border border-slate-700 flex items-center justify-center hover:bg-[#0EA5E9] hover:border-[#0EA5E9] transition-colors duration-300"
                >
                  <Linkedin size={18} />
                </a>
                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="twitter-link"
                  className="w-10 h-10 border border-slate-700 flex items-center justify-center hover:bg-[#0EA5E9] hover:border-[#0EA5E9] transition-colors duration-300"
                >
                  <Twitter size={18} />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-['Outfit'] font-semibold text-lg mb-6">Quick Links</h3>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-slate-400 hover:text-[#0EA5E9] text-sm transition-colors duration-200 flex items-center gap-2 group"
                    >
                      <ArrowRight size={14} className="opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all duration-200" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h3 className="font-['Outfit'] font-semibold text-lg mb-6">Our Services</h3>
              <ul className="space-y-3">
                {services.map((service) => (
                  <li key={service}>
                    <Link
                      to="/services"
                      className="text-slate-400 hover:text-[#0EA5E9] text-sm transition-colors duration-200"
                    >
                      {service}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="font-['Outfit'] font-semibold text-lg mb-6">Contact Us</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPin size={18} className="text-[#0EA5E9] mt-0.5 flex-shrink-0" />
                  <span className="text-slate-400 text-sm">
                    Silversquare, 523 Avenue Louise<br />
                    1050 Bruxelles, Belgique
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone size={18} className="text-[#0EA5E9] flex-shrink-0" />
                  <a
                    href="tel:+32496796461"
                    className="text-slate-400 hover:text-white text-sm transition-colors duration-200"
                  >
                    +32 496 79 64 61
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail size={18} className="text-[#0EA5E9] flex-shrink-0" />
                  <a
                    href="mailto:jemimabolokwa@supply-hub.eu"
                    className="text-slate-400 hover:text-white text-sm transition-colors duration-200"
                  >
                    jemimabolokwa@supply-hub.eu
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-slate-500 text-sm">
                © {currentYear} Supply Hub. All rights reserved.
              </p>
              <div className="flex gap-6">
                <Link to="/privacy-policy" className="text-slate-500 hover:text-slate-400 text-sm transition-colors duration-200">
                  Privacy Policy
                </Link>
                <Link to="/terms-of-service" className="text-slate-500 hover:text-slate-400 text-sm transition-colors duration-200">
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
